import { onlineManager, useQueryClient } from '@tanstack/react-query';
import { logger } from 'lib/hatchd-utils';
import { Button, Loader, Text } from 'components/ui';
import { useBuyerOrg } from 'hooks/use-buyer-org';
import { usePwaConfig } from 'hooks/use-pwa-config';
import { useStore } from 'lib/store';
import { NextPage } from 'next';
import { signIn, useSession } from 'next-auth/react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { PATHS, SAFE_PATHS_NO_ORG } from 'settings/config';
import styled from 'styled-components';

type Options = {
  hideHeader?: boolean;
  hideNav?: boolean;
  overrideHeaderContent?: boolean;
  hideHomeLink?: boolean;
  marketplace?: boolean;
  app?: boolean;
};

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

/**
 * How long the auth gate may sit on the loading state before we assume
 * something is wrong and offer the user a way out. Generous enough to cover a
 * slow rural connection, short enough that nobody stares at a dead spinner.
 */
const STALLED_AFTER_MS = 8000;

/** Protect this page and prevent rendering if the current user session is invalid */
export function withAuth<T extends {} = {}>(
  WrappedComponent: NextPage<T>,
  options?: Options
) {
  // Try to create a nice displayName for React Dev Tools.
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';

  // Creating the inner component. The calculated Props type here is the where the magic happens.
  const ComponentWithAuth = (props: T) => {
    const { data: session, status } = useSession();
    const loading = status === 'loading';
    const router = useRouter();
    const activeBuyerOrgId = useStore((store) => store.activeBuyerOrgId);
    const setBuyerOrgId = useStore((store) => store.setActiveBuyerOrgId);
    const { data: buyerOrg } = useBuyerOrg();
    const queryClient = useQueryClient();
    const setIsAppRedirect = useStore((store) => store.setIsAppRedirect);
    const { config: pwaConfig, isLoading: pwaLoading } = usePwaConfig();

    useEffect(() => {
      if (router.asPath.includes('?app=true')) {
        setIsAppRedirect(true);
      }
    }, [router.asPath]);

    useEffect(() => {
      if (!buyerOrg && !activeBuyerOrgId && router?.query?.buyerOrgId) {
        setBuyerOrgId(router.query.buyerOrgId as string);
      }
    }, [router.query.buyerOrgId]);

    // Redirect the user to the org picker page if there is no active buyer org ID
    useEffect(() => {
      if (
        !pwaLoading &&
        !activeBuyerOrgId &&
        !SAFE_PATHS_NO_ORG.includes(router.pathname) &&
        session?.accessToken &&
        !pwaConfig?.org_id
      ) {
        console.log('Redirect to org picker');
        logger.info('No buyer org set, redirecting to org picker page');
        router.push(PATHS.organisation.picker);
      }
    }, [
      activeBuyerOrgId,
      router.pathname,
      session?.accessToken,
      pwaConfig,
      pwaLoading,
    ]);

    // Redirect to login page, user is not authenticated
    useEffect(() => {
      if (!session && !loading) {
        signIn();
      }
    }, [loading, session]);

    // The gate below renders a spinner until the session and the active buyer
    // org have both resolved. Neither has a timeout of its own, so anything
    // that stops those queries from settling — a browser wrongly reporting
    // itself offline, a backend outage, a dropped connection — leaves the user
    // on an unbounded spinner with no way to recover short of restarting the
    // browser. Surface it instead, and give them a retry. (Fixes LPD-5650)
    const [stalled, setStalled] = useState(false);
    const ready =
      !!session && (!!buyerOrg || SAFE_PATHS_NO_ORG.includes(router.pathname));

    useEffect(() => {
      // Not stalled if we are already on our way somewhere: the signIn()
      // redirect above owns the unauthenticated case.
      if (ready || (!session && !loading)) {
        setStalled(false);
        return;
      }

      const timeout = setTimeout(() => {
        logger.error('Auth gate stalled: session or buyer org never resolved');
        setStalled(true);
      }, STALLED_AFTER_MS);

      return () => clearTimeout(timeout);
    }, [ready, session, loading]);

    const handleRetry = useCallback(() => {
      setStalled(false);
      // The browser may still be claiming it is offline; overrule it and let
      // the requests themselves report the truth.
      onlineManager.setOnline(true);
      queryClient.refetchQueries();
    }, [queryClient]);

    // Keep authenticated pages out of search indexes. robots.txt is only a
    // crawl hint; this noindex directive is the actual instruction not to
    // index, and it travels with the page regardless of robots.txt rules.
    const noIndex = (
      <Head>
        <meta name='robots' content='noindex, nofollow' />
      </Head>
    );

    // Render the passed in component
    if (ready) {
      return (
        <>
          {noIndex}
          <WrappedComponent {...props} />
        </>
      );
    }

    if (stalled) {
      return (
        <>
          {noIndex}
          <Wrapper>
            <Text textAlign={'center'} fontSize={5}>
              Having trouble connecting
            </Text>
            <Text textAlign={'center'} verticalSpacing={3}>
              We couldn&apos;t reach Agora. Check your connection and try again.
            </Text>
            <Button onClick={handleRetry}>Try again</Button>
          </Wrapper>
        </>
      );
    }

    return (
      <>
        {noIndex}
        <Wrapper>
          <Text textAlign={'center'} fontSize={5}>
            Authenticating
          </Text>
          <Loader size={5} />
        </Wrapper>
      </>
    );
  };

  ComponentWithAuth.displayName = `withAuth(${displayName})`;
  ComponentWithAuth.overrideHeaderContent = options?.overrideHeaderContent;
  ComponentWithAuth.hideNav = !!options?.hideNav;
  ComponentWithAuth.hideHeader = !!options?.hideHeader;
  ComponentWithAuth.hideHomeLink = !!options?.hideHomeLink;
  ComponentWithAuth.marketplace = !!options?.marketplace;
  ComponentWithAuth.app = !!options?.app;

  return ComponentWithAuth;
}
